import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDocs, getFirestore, setDoc } from 'firebase/firestore';
import { Calendar, ExternalLink, Mail, Phone, Trash2, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import DataGridLayout from '../../components/layout/DataGridLayout';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import VerificationToggle from '../../components/ui/VerificationToggle';
import { useToast } from '../../hooks/use-toast';
import { EnquiryRequest } from '../../types/enquiry';
import { app } from '../../firebase';

// Sample data from Firebase Queries table  
const MOCK_ENQUIRIES: EnquiryRequest[] = [
  {
    id: '0SEyBK2VTMflDhDEH8LV',
    emailId: 'chiranjeevisarvepalli0105@gmail.com',
    mobileNo: '+918978570105',
    fullName: 'Chiranjeevi Sarvepalli',
    isVerified: true,
    createdAt: Date.now() - 2 * 24 * 60 * 60 * 1000, // 2 days ago  
  },
  {
    id: '1',
    emailId: 'rocky11noone@gmail.com',
    mobileNo: '+918217390087',
    fullName: 'Sachhin',
    isVerified: true,
    createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000, // 5 days ago  
  },
  {
    id: '2',
    emailId: 'salmansid397@gmail.com',
    mobileNo: '+919380291674',
    fullName: 'Salman',
    isVerified: true,
    createdAt: Date.now() - 7 * 24 * 60 * 60 * 1000, // 7 days ago  
  },
  {
    id: '3',
    emailId: 'nitenkumar58@gmail.com',
    mobileNo: '+916006247815',
    fullName: 'Nitin Kumar',
    isVerified: true,
    createdAt: Date.now() - 10 * 24 * 60 * 60 * 1000, // 10 days ago  
  },
  {
    id: '4',
    emailId: 'kandukuriyugandhar21@gmail.com',
    mobileNo: '+917660840634',
    fullName: 'K.yugandhar',
    isVerified: false,
    createdAt: Date.now() - 1 * 24 * 60 * 60 * 1000, // 1 day ago  
  },
  {
    id: '5',
    emailId: 'kadivetsrihari6@gmail.com',
    mobileNo: '+919963675531',
    fullName: 'Srihari',
    isVerified: true,
    createdAt: Date.now() - 3 * 24 * 60 * 60 * 1000, // 3 days ago  
  },
  {
    id: '6',
    emailId: 'shaheenheena26@gmail.com',
    mobileNo: '+916309066883',
    fullName: 'Jp Shaheena Begum',
    isVerified: true,
    createdAt: Date.now() - 15 * 24 * 60 * 60 * 1000, // 15 days ago  
  },
];

const EnquiryRequests = () => {
  // const [enquiries, setEnquiries] = useState<EnquiryRequest[]>(MOCK_ENQUIRIES);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState<EnquiryRequest | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const db = getFirestore(app);
  const auth = getAuth(app);
  const { toast } = useToast();
  const [enquires, setEnquiries] = useState([]);

  // Define table columns  
  const columns: ColumnDef<EnquiryRequest>[] = [
    {
      accessorKey: 'fullName',
      header: 'Full Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-purple-500" />
          <span className="font-medium">{row.getValue('fullName')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'emailId',
      header: 'Email ID',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-500" />
          <span>{row.getValue('emailId')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'mobileNo',
      header: 'Mobile No',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-green-500" />
          <span>{row.getValue('mobileNo')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'isVerified',
      header: 'Is Verified',
      cell: ({ row }) => (
        <div className="flex justify-center">
          <VerificationToggle
            isVerified={row.original.isVerified}
            onToggle={() => handleToggleVerification(row.original.id)}
          />
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleViewDetails(row.original)}
            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
          >
            <ExternalLink size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDeleteClick(row.original)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 size={18} />
          </Button>
        </div>
      ),
    }
  ];

  useEffect(() => {
    // Inside your component  
    fetchEnquires();
  }, [])


  const fetchEnquires = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Queries'));
      const enquiriesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setEnquiries(enquiriesData);
    } catch (error) {
      toast({
        title: "Error Fetching Enquiries",
        description: error.message,
        variant: "destructive",
      });
    }
  };
  const handleViewDetails = (enquiry: EnquiryRequest) => {
    setSelectedEnquiry(enquiry);
  };

  const handleDeleteClick = (enquiry: EnquiryRequest) => {
    setSelectedEnquiry(enquiry);
    setShowDeleteConfirm(true);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    fetchEnquires();
    // Simulate API call  
    setTimeout(() => {
      setIsLoading(false);
      toast({
        title: "Refreshed",
        description: "Enquiry data has been refreshed successfully.",
      });
    }, 800);
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Your enquiry data export has been initiated.",
    });
  };

  const handleToggleVerification = async (id: string) => {
    setEnquiries(prevEnquiries =>
      prevEnquiries.map(enquiry =>
        enquiry.id === id
          ? { ...enquiry, isVerified: !enquiry.isVerified }
          : enquiry
      )
    );
    let record: any = enquires.filter(x => x.id == id)[0];
    record = {
      ...record,
      isVerified: !record.isVerified
    }
    try {
      const courseRef = doc(db, "Queries", id); // Get a reference to the course document  
      await setDoc(courseRef, record); // Update the course data in Firestore  
      toast({
        title: "Verification Status Updated",
        description: "The verification status has been updated successfully.",
      });

    } catch (error) {
      toast({
        title: "Verification Failed",
        description: "The verification updation has been failed."
      });
    }


  };

  const handleDelete = () => {
    if (!selectedEnquiry) return;

    setEnquiries(prevEnquiries => prevEnquiries.filter(enquiry => enquiry.id !== selectedEnquiry.id));

    toast({
      title: "Entry Deleted",
      description: "The enquiry has been removed successfully.",
    });

    setShowDeleteConfirm(false);
    setSelectedEnquiry(null);
  };

  // Filter the enquiries based on search query  
  const filteredEnquiries = enquires?.filter(enquiry =>
    enquiry?.fullName.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    enquiry?.emailId.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    enquiry?.mobileNo.includes(searchQuery)
  );

  return (
    <>
      <DataGridLayout
        title="Enquiry Requests"
        subtitle="Manage student enquiries and their verification status"
        description="Track new enquiries, verify identities, and manage follow-ups"
        searchPlaceholder="Search by name, email or phone..."
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={() => toast({
          title: "Add Functionality",
          description: "Add new enquiry feature would be implemented here.",
        })}
      >
        <DataTable
          columns={columns}
          data={filteredEnquiries}
          noResultsMessage="No enquiries found"
          isLoading={isLoading}
        />
      </DataGridLayout>

      {/* Enquiry Details Dialog */}
      {selectedEnquiry && (
        <Dialog open={!!selectedEnquiry && !showDeleteConfirm} onOpenChange={() => setSelectedEnquiry(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Enquiry Details</DialogTitle>
              <DialogDescription>
                Complete information about the selected enquiry.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input id="name" value={selectedEnquiry.fullName} readOnly className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email
                </Label>
                <Input id="email" value={selectedEnquiry.emailId} readOnly className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="phone" className="text-right">
                  Phone
                </Label>
                <Input id="phone" value={selectedEnquiry.mobileNo} readOnly className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="status" className="text-right">
                  Status
                </Label>
                <div className="col-span-3">
                  <VerificationToggle
                    isVerified={selectedEnquiry.isVerified}
                    onToggle={() => handleToggleVerification(selectedEnquiry?.id)}
                  />
                </div>
              </div>
              {/* <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="created" className="text-right">
                  Created
                </Label>
                <Input
                  id="created"
                  value={format(new Date(selectedEnquiry?.createdAt ? ), )}
                  readOnly
                  className="col-span-3"
                />
              </div> */}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedEnquiry(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this enquiry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedEnquiry && (
              <div className="text-center p-4 border rounded-md bg-muted/50">
                <p className="font-semibold">{selectedEnquiry.fullName}</p>
                <p className="text-sm text-muted-foreground">{selectedEnquiry.emailId}</p>
                <p className="text-sm text-muted-foreground">{selectedEnquiry.mobileNo}</p>
              </div>
            )}
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnquiryRequests;  